import React, { useEffect, useState } from 'react';
import {
  AreaChart, Area, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import api from '../utils/api';
import './DashboardHome.css';

interface Stats {
  totalBookings: number;
  flightBookings: number;
  hotelBookings: number;
  totalRevenue: number;
  activeUsers: number;
  pendingInvoices: number;
  recentActivity: Activity[];
  bookingTrends: BookingTrend[];
  revenueByType: RevenueType[];
  topHotels: TopHotel[];
}

interface Activity {
  id: number;
  type: 'booking' | 'invoice' | 'user';
  description: string;
  time: string;
  status?: string;
  amount?: number;
}

interface BookingTrend {
  date: string;
  flights: number;
  hotels: number;
  revenue: number;
}

interface RevenueType {
  name: string;
  value: number;
  color: string;
}

interface TopHotel {
  name: string;
  bookings: number;
  revenue: number;
}

const DashboardHome: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalBookings: 0,
    flightBookings: 0,
    hotelBookings: 0,
    totalRevenue: 0,
    activeUsers: 0,
    pendingInvoices: 0,
    recentActivity: [],
    bookingTrends: [],
    revenueByType: [],
    topHotels: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [flightRes, hotelRes, usersRes, invoicesRes] = await Promise.allSettled([
        api.getFlightOrders(1, 100),
        api.getHotelOrders(1, 100),
        api.getUsers(1, 100),
        api.getInvoicesFromServer(1, 500)
      ]);

      const flightOrders = (flightRes.status === 'fulfilled' ? flightRes.value.flight_orders : []) as any[];
      const hotelOrders = (hotelRes.status === 'fulfilled' ? hotelRes.value.hotel_orders : []) as any[];
      const users = (usersRes.status === 'fulfilled' ? usersRes.value.users : []) as any[];
      const invoices = (invoicesRes.status === 'fulfilled' ? invoicesRes.value.invoices : []) as any[];

      const totalFlightBookings = flightOrders.length;
      const totalHotelBookings = hotelOrders.length;
      const totalBookings = totalFlightBookings + totalHotelBookings;

      const flightRevenue = flightOrders.reduce((sum: number, f: any) => sum + (Number(f.total_price) || 0), 0);
      const hotelRevenue = hotelOrders.reduce((sum: number, h: any) => sum + (Number(h.total_price) || 0), 0);
      const totalRevenue = flightRevenue + hotelRevenue;

      const pendingInvoices = invoices.filter((i: any) => i.status === 'pending').length;
      const activeUsers = users.filter((u: any) => u.status === 'active').length;

      const trends = generateBookingTrends(flightOrders, hotelOrders, timeRange);

      const revenueByType = [
        { name: 'Flights', value: flightRevenue, color: '#f43b47' },
        { name: 'Hotels', value: hotelRevenue, color: '#453a94' }
      ];

      // Top hotels from hotel orders
      const hotelsMap = new Map<string, { bookings: number; revenue: number }>();
      hotelOrders.forEach((h: any) => {
        const name = h.hotel_name || 'Unknown Hotel';
        const current = hotelsMap.get(name) || { bookings: 0, revenue: 0 };
        current.bookings += 1;
        current.revenue += Number(h.total_price) || 0;
        hotelsMap.set(name, current);
      });
      const topHotels = Array.from(hotelsMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      const recentActivity = generateRecentActivity(flightOrders, hotelOrders, invoices);

      setStats({
        totalBookings,
        flightBookings: totalFlightBookings,
        hotelBookings: totalHotelBookings,
        totalRevenue,
        activeUsers,
        pendingInvoices,
        recentActivity,
        bookingTrends: trends,
        revenueByType,
        topHotels
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateBookingTrends = (flights: any[], hotels: any[], range: string): BookingTrend[] => {
    const days = range === 'week' ? 7 : range === 'month' ? 30 : 90;
    const trends: BookingTrend[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const dayFlights = flights.filter((f: any) => {
        const fDate = new Date(f.created_at);
        return fDate.toDateString() === date.toDateString();
      }).length;

      const dayHotels = hotels.filter((h: any) => {
        const hDate = new Date(h.created_at || h.check_in_date);
        return hDate.toDateString() === date.toDateString();
      }).length;

      const dayRevenue = [...flights, ...hotels]
        .filter((b: any) => {
          const bDate = new Date(b.created_at || b.check_in_date);
          return bDate.toDateString() === date.toDateString();
        })
        .reduce((sum: number, b: any) => sum + (Number(b.total_price) || 0), 0);

      trends.push({
        date: dateStr,
        flights: dayFlights,
        hotels: dayHotels,
        revenue: dayRevenue
      });
    }

    return trends;
  };

  const generateRecentActivity = (flights: any[], hotels: any[], invoices: any[]): Activity[] => {
    const activities: Activity[] = [];

    flights.slice(0, 5).forEach((f: any) => {
      activities.push({
        id: f.booking_id || Math.random(),
        type: 'booking',
        description: `Flight booked: ${f.pnr} (${f.first_name} ${f.last_name})`,
        time: new Date(f.created_at).toLocaleString(),
        status: f.status,
        amount: Number(f.total_price)
      });
    });

    hotels.slice(0, 5).forEach((h: any) => {
      activities.push({
        id: h.hotel_order_id || Math.random(),
        type: 'booking',
        description: `Hotel booked: ${h.hotel_name || 'Unknown'}`,
        time: new Date(h.created_at || h.check_in_date).toLocaleString(),
        status: h.status,
        amount: Number(h.total_price)
      });
    });

    invoices.slice(0, 5).forEach((i: any) => {
      activities.push({
        id: i.id || Math.random(),
        type: 'invoice',
        description: `Invoice #${i.id} generated`,
        time: new Date(i.createdAt).toLocaleString(),
        amount: i.amount
      });
    });

    return activities
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 10);
  };

  const StatCard = ({ title, value, icon, trend, color }: any) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ background: color + '20', color }}>{icon}</div>
      <div className="stat-content">
        <h3>{title}</h3>
        <div className="stat-value">{value}</div>
        {trend && <div className="stat-trend">{trend}</div>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      {/* <div className="dashboard-header">
        <div>
          <h1>Dashboard Overview</h1>
          <p>Welcome back! Here's what's happening with your business today.</p>
        </div>
        <div className="time-range-selector">
          <button className={timeRange === 'week' ? 'active' : ''} onClick={() => setTimeRange('week')}>Week</button>
          <button className={timeRange === 'month' ? 'active' : ''} onClick={() => setTimeRange('month')}>Month</button>
          <button className={timeRange === 'quarter' ? 'active' : ''} onClick={() => setTimeRange('quarter')}>Quarter</button>
        </div>
      </div> */}

      <div className="stats-grid">
        <StatCard title="Total Bookings" value={stats.totalBookings.toLocaleString()} icon="📊" trend={`+${stats.flightBookings} flights, +${stats.hotelBookings} hotels`} color="#f43b47" />
        <StatCard title="Total Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} icon="💰" trend="Last 30 days" color="#453a94" />
        <StatCard title="Active Users" value={stats.activeUsers.toLocaleString()} icon="👥" trend="+12% this week" color="#10b981" />
        <StatCard title="Pending Invoices" value={stats.pendingInvoices.toLocaleString()} icon="📄" trend="Awaiting payment" color="#f59e0b" />
      </div>

      <div className="charts-row">
        <div className="chart-card large">
          <div className="chart-header">
            <h3>Booking Trends</h3>
            <div className="chart-legend">
              <span><span className="dot" style={{ background: '#f43b47' }}></span> Flights</span>
              <span><span className="dot" style={{ background: '#453a94' }}></span> Hotels</span>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={stats.bookingTrends}>
                <defs>
                  <linearGradient id="colorFlights" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43b47" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f43b47" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorHotels" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#453a94" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#453a94" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip contentStyle={{ background: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="flights" stroke="#f43b47" fillOpacity={1} fill="url(#colorFlights)" strokeWidth={2} />
                <Area type="monotone" dataKey="hotels" stroke="#453a94" fillOpacity={1} fill="url(#colorHotels)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <h3>Revenue Distribution</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.revenueByType}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {stats.revenueByType.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(value: any) => `₹${value.toLocaleString()}`} contentStyle={{ background: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="chart-card">
          <h3>Revenue Trend</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={stats.bookingTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip formatter={(value: any) => `₹${value.toLocaleString()}`} contentStyle={{ background: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <h3>Top Performing Hotels</h3>
          <div className="top-list">
            {stats.topHotels.map((hotel, index) => (
              <div key={index} className="top-item">
                <div className="top-rank">{index + 1}</div>
                <div className="top-info">
                  <div className="top-name">{hotel.name}</div>
                  <div className="top-stats">
                    <span>{hotel.bookings} bookings</span>
                    <span>₹{hotel.revenue.toLocaleString()}</span>
                  </div>
                </div>
                <div className="top-progress">
                  <div className="top-progress-bar" style={{ width: `${(hotel.revenue / stats.topHotels[0]?.revenue) * 100}%`, background: '#453a94' }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="activity-card">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {stats.recentActivity.map((activity, index) => (
            <div key={index} className="activity-item">
              <div className={`activity-icon ${activity.type}`}>
                {activity.type === 'booking' ? '✈️' : activity.type === 'invoice' ? '📄' : '👤'}
              </div>
              <div className="activity-content">
                <div className="activity-description">{activity.description}</div>
                <div className="activity-meta">
                  <span className="activity-time">{activity.time}</span>
                  {activity.status && <span className={`activity-status ${activity.status.toLowerCase()}`}>{activity.status}</span>}
                  {activity.amount && <span className="activity-amount">₹{activity.amount.toLocaleString()}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;